import { Page } from 'componentregistry';
import { selector } from '@cloudio-saas/statex';
import { expandParentsAction } from 'layout/Sidebar/expandParentsAction';
import { TreeType } from 'layout/Sidebar/panels/TreeView/state';
import { Platform } from 'componentregistry/platform/Platform';
import {
  activeItemIdAtom,
  activeItemIdsAtom,
  activeItemPathAtom,
  designPath,
  pageAtom,
} from './atoms';

const EMPTY_ARRAY = Object.freeze([]) as unknown as string[];
const ROOT_ARRAY = Object.freeze(['root']) as unknown as string[];

export const activeItemSelector = selector<
  {
    appUid: string;
    itemId: string;
    pageId: string;
    expand: boolean;
    treename: TreeType;
    event?: MouseEvent;
  },
  Platform
>({
  path: ['transient', ':treename', ':appUid', ':pageId', 'activeItemSelector'],
  get: ({ get, params }, platform) => {
    let pageId = platform.pageId;
    if (!params)
      return {
        appUid: platform.appUid,
        pageId,
        itemId: 'root',
        expand: false,
        treename: 'designer',
      };
    const treename = params.treename as TreeType;
    pageId = params.pageId as string;
    const appUid = params.appUid as string;
    const itemId =
      get(activeItemIdAtom, {
        params: { appUid, pageId, treename },
      }) || 'root';
    return { appUid, pageId, itemId, expand: false, treename };
  },
  set: (
    { set, get, call, props: platform },
    { appUid, pageId, itemId, expand = true, treename, event },
  ) => {
    if (itemId) {
      const activeItemId = get(activeItemIdAtom, {
        params: { appUid, pageId, treename },
      });
      if (activeItemId === itemId) {
        // avoid changing the state if the activeItemId is not changed
        return { appUid, pageId, itemId, expand, treename };
      }
      if (event && (event.shiftKey || event.ctrlKey || event.metaKey)) {
        let ids: string[] | undefined;
        if (event.shiftKey) {
          const page = get(pageAtom, { params: { appUid, pageId } }) as Page;
          const activeItem = page[activeItemId];
          const currentItem = page[itemId];
          if (activeItem.parent === currentItem.parent) {
            const { children } = page[activeItem.parent];
            const fromIndex = children.indexOf(activeItemId);
            const toIndex = children.indexOf(itemId);
            ids =
              fromIndex < toIndex
                ? children.slice(fromIndex, toIndex + 1)
                : children.slice(toIndex, fromIndex + 1);
            set(activeItemIdsAtom, ids, {
              params: { appUid, pageId },
            });
          }
        }
        if (!ids) {
          ids = get(activeItemIdsAtom, {
            params: { appUid, pageId },
          });
          if (ids.length) {
            ids = [...ids, itemId];
          } else {
            ids = [activeItemId, itemId];
          }
        }
        set(activeItemIdsAtom, ids, {
          params: { appUid, pageId },
        });
      } else {
        set(activeItemIdsAtom, (ids) => (ids.length ? EMPTY_ARRAY : ids), {
          params: { appUid, pageId },
        });
        set(activeItemIdAtom, itemId, { params: { appUid, pageId, treename } });
        if (expand) {
          const page = get(designPath({ appUid, pageId })) as Page;
          const path = [itemId];
          let parent = page[itemId]?.parent;
          while (parent) {
            path.push(parent);
            if (parent === 'root') {
              break;
            }
            parent = page[parent]?.parent;
          }
          set(activeItemPathAtom, path, {
            params: { appUid, pageId, treename },
          });
        } else {
          if (itemId !== 'root') {
            call(expandParentsAction, { appUid, pageId, itemId, treename });
          }
          set(activeItemPathAtom, [], { params: { appUid, pageId, treename } });
        }
      }
    } else {
      set(activeItemIdAtom, '', { params: { appUid, pageId, treename } });
      set(activeItemPathAtom, ROOT_ARRAY, {
        params: { appUid, pageId, treename },
      });
    }
    return { appUid, pageId, itemId, expand, treename };
  },
});
